/**
 * Snaps a typed quantity to a valid batch multiple, clamped to [unitMultiplier, flooredMax].
 * All math runs in multiplied (unit) space; callers convert to batch count via `result / unitMultiplier`.
 * Ties round DOWN (15 → 12, 9 → 6) and the max is floored so it is always a multiple of the batch size.
 * Empty / non-numeric / nullish input reverts to `current` (the value before the edit).
 * When `max` is undefined the upper clamp is skipped (always-in-stock: no inventory ceiling).
 */
export const snapQuantity = (
  typed: number | string | null | undefined,
  { unitMultiplier, max, current }: { unitMultiplier: number; max?: number; current: number }
): number => {
  if (typed === null || typed === undefined || String(typed).trim() === '') {
    return current;
  }
  const value = Number(typed);
  if (!Number.isFinite(value) || unitMultiplier <= 0) {
    return current;
  }
  const snapped = Math.ceil(value / unitMultiplier - 0.5) * unitMultiplier;
  if (max === undefined || !Number.isFinite(max)) {
    return Math.max(snapped, unitMultiplier);
  }
  // Floored max never drops below one batch, so the result can never be 0 even when
  // remaining stock is smaller than a single batch (the parent treats 0 as "remove item").
  const maxMultiple = Math.max(Math.floor(max / unitMultiplier) * unitMultiplier, unitMultiplier);
  return Math.min(Math.max(snapped, unitMultiplier), maxMultiple);
};
